import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Encounter, LabRequest, RadiologyRequest } from '../../types';
import { Search, FileText, FlaskConical, Scan, Printer, Download, X } from 'lucide-react';
import { fbcStructure, urinalysisTests, testosteroneTests, hormonalProfileTests, amhStructure, isFBCTest, isUrinalysisTest, isTestosteroneTest, isHormonalProfileTest, isAMHTest } from '../../store/labStructures';
import jsPDF from 'jspdf';
interface Props {}
export const PatientReports: React.FC<Props> = () => {
  const {
    encounters,
    patients
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewing, setViewing] = useState<{
    encounter: Encounter;
  } | null>(null);
  // Encounters that have at least one completed lab OR a radiology report
  const reportEncounters = useMemo(() => {
    return encounters.filter(e => {
      const hasCompletedLab = e.labRequests.some(l => l.status === 'Completed');
      const hasRadiologyReport = e.radiologyRequests.some(r => r.report || r.images && r.images.length > 0);
      return hasCompletedLab || hasRadiologyReport;
    }).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  }, [encounters]);
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return reportEncounters;
    return reportEncounters.filter(e => {
      const p = patients.find(pt => pt.id === e.patientId);
      if (!p) return false;
      return p.firstName.toLowerCase().includes(q) || p.lastName.toLowerCase().includes(q) || p.bmcFileNumber.toLowerCase().includes(q);
    });
  }, [reportEncounters, patients, searchQuery]);
  const getPatient = (id: string) => patients.find(p => p.id === id);
  const handlePrint = () => window.print();
  const handleDownloadPDF = () => {
    if (!viewing) return;
    const patient = getPatient(viewing.encounter.patientId);
    if (!patient) return;
    const pdf = new jsPDF({
      unit: 'pt',
      format: 'a4'
    });
    const margin = 40;
    let y = margin;
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Patient Report', margin, y);
    y += 22;
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`${patient.firstName} ${patient.lastName} · BMC ${patient.bmcFileNumber} · ${patient.age} yrs · ${patient.sex}`, margin, y);
    y += 14;
    pdf.text(`Visit date: ${new Date(viewing.encounter.startedAt).toLocaleString()}`, margin, y);
    y += 20;
    const lineCheck = () => {
      const ph = pdf.internal.pageSize.getHeight() - margin;
      if (y > ph) {
        pdf.addPage();
        y = margin;
      }
    };
    // ── Laboratory results ──
    const completedLabs = viewing.encounter.labRequests.filter(l => l.status === 'Completed');
    if (completedLabs.length > 0) {
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Laboratory Results', margin, y);
      y += 16;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      completedLabs.forEach(lab => {
        lineCheck();
        pdf.setFont('helvetica', 'bold');
        const activeTests = lab.tests.filter(t => !(lab.removedTestNames || []).includes(t));
        pdf.text(`Tests: ${activeTests.join(', ')}`, margin, y);
        y += 12;
        pdf.setFont('helvetica', 'normal');
        if (lab.completedAt) {
          pdf.text(`Completed: ${new Date(lab.completedAt).toLocaleString()}`, margin, y);
          y += 12;
        }
        if (lab.results) {
          Object.entries(lab.results).forEach(([k, v]) => {
            if (!v) return;
            lineCheck();
            const line = `  ${k}: ${v}`;
            const lines = pdf.splitTextToSize(line, 515);
            pdf.text(lines, margin, y);
            y += lines.length * 11;
          });
        }
        if (lab.otherTests) {
          lineCheck();
          pdf.text(`  Other: ${lab.otherTests}`, margin, y);
          y += 12;
        }
        if (lab.labComments) {
          lineCheck();
          pdf.setFont('helvetica', 'bold');
          pdf.text('Comments / Observations:', margin, y);
          y += 12;
          pdf.setFont('helvetica', 'normal');
          const lines = pdf.splitTextToSize(lab.labComments, 515);
          lines.forEach((line: string) => {
            lineCheck();
            pdf.text(line, margin, y);
            y += 11;
          });
        }
        y += 6;
      });
    }
    // ── Radiology results ──
    const radWithReport = viewing.encounter.radiologyRequests.filter(r => r.report);
    if (radWithReport.length > 0) {
      lineCheck();
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Radiology Reports', margin, y);
      y += 16;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      radWithReport.forEach(r => {
        lineCheck();
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Scan: ${r.scanType}`, margin, y);
        y += 12;
        pdf.setFont('helvetica', 'normal');
        if (r.completedAt) {
          pdf.text(`Completed: ${new Date(r.completedAt).toLocaleString()}`, margin, y);
          y += 12;
        }
        const lines = pdf.splitTextToSize(r.report || '', 515);
        lines.forEach((line: string) => {
          lineCheck();
          pdf.text(line, margin, y);
          y += 11;
        });
        y += 8;
      });
    }
    pdf.save(`report-${patient.bmcFileNumber}-${new Date(viewing.encounter.startedAt).toISOString().slice(0, 10)}.pdf`);
  };
  // ── Renderers for the View modal ──
  const renderLabResults = (lab: LabRequest) => {
    if (!lab.results) return null;
    const activeTests = lab.tests.filter(t => !(lab.removedTestNames || []).includes(t));
    const focusedTest = activeTests[0];
    if (isFBCTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-5296">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5297">
            FBC Report
          </h4>
          {fbcStructure.map(cat => <div key={cat.category} className="mb-3" data-id="element-5298">
              <p className="font-semibold text-gray-700 mb-1" data-id="element-5299">{cat.category}</p>
              <table className="w-full text-xs border border-gray-300" data-id="element-5300">
                <thead className="bg-gray-100" data-id="element-5301">
                  <tr data-id="element-5302">
                    <th className="border border-gray-300 p-1.5 text-left" data-id="element-5303">
                      Test
                    </th>
                    <th className="border border-gray-300 p-1.5 text-left w-24" data-id="element-5304">
                      Result
                    </th>
                    <th className="border border-gray-300 p-1.5 text-left" data-id="element-5305">
                      Reference
                    </th>
                    <th className="border border-gray-300 p-1.5 text-left w-20" data-id="element-5306">
                      Unit
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-5307">
                  {cat.tests.map(t => <tr key={t.name} data-id="element-5308">
                      <td className="border border-gray-300 p-1.5" data-id="element-5309">{t.name}</td>
                      <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5310">
                        {lab.results![t.name] || '—'}
                      </td>
                      <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5311">
                        {t.range}
                      </td>
                      <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5312">
                        {t.unit}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>)}
        </div>;
    }
    if (isUrinalysisTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-5313">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5314">
            Urinalysis Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-5315">
            <thead className="bg-gray-100" data-id="element-5316">
              <tr data-id="element-5317">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5318">
                  Parameter
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-32" data-id="element-5319">
                  Result
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5320">
                  Reference
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5321">
              {urinalysisTests.map(t => <tr key={t.name} data-id="element-5322">
                  <td className="border border-gray-300 p-1.5" data-id="element-5323">{t.name}</td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5324">
                    {lab.results![`UA_${t.name}`] || '—'}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5325">
                    {t.normal || '—'}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>;
    }
    if (isTestosteroneTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-5326">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5327">
            Testosterone Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-5328">
            <thead className="bg-gray-100" data-id="element-5329">
              <tr data-id="element-5330">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5331">Test</th>
                <th className="border border-gray-300 p-1.5 text-left w-28" data-id="element-5332">
                  Result
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5333">
                  Reference (M)
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5334">
                  Reference (F)
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-16" data-id="element-5335">
                  Unit
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5336">
              {testosteroneTests.map(t => <tr key={t.name} data-id="element-5337">
                  <td className="border border-gray-300 p-1.5" data-id="element-5338">{t.name}</td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5339">
                    {lab.results![`TESTO_${t.name}`] || '—'}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5340">
                    {t.rangeMale}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5341">
                    {t.rangeFemale}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5342">
                    {t.unit}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>;
    }
    if (isAMHTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-5343">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5344">
            Anti Mullerian Hormone (AMH) Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-5345">
            <thead className="bg-gray-100" data-id="element-5346">
              <tr data-id="element-5347">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5348">Test</th>
                <th className="border border-gray-300 p-1.5 text-left w-28" data-id="element-5349">
                  Value
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5350">
                  Reference (by age)
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-16" data-id="element-5351">
                  Unit
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5352">
              <tr className="align-top" data-id="element-5353">
                <td className="border border-gray-300 p-1.5 font-medium" data-id="element-5354">
                  {amhStructure.name}
                </td>
                <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5355">
                  {lab.results![amhStructure.storageKey] || focusedTest && lab.results![focusedTest] || lab.results!['AMH'] || lab.results!['Anti-Müllerian Hormone'] || lab.results!['ANTI-MÜLLERIAN HORMONE'] || lab.results!['Anti Mullerian Hormone (AMH)'] || '—'}
                </td>
                <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5356">
                  {amhStructure.ageRanges.map(a => <div key={a.ageBand} data-id="element-5357">
                      <span className="font-medium" data-id="element-5358">{a.ageBand}:</span>{' '}
                      {a.range}
                    </div>)}
                </td>
                <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5359">
                  {amhStructure.unit}
                </td>
              </tr>
            </tbody>
          </table>
          <p className="mt-2 text-[10px] italic text-gray-600" data-id="element-5360">
            Method: {amhStructure.method}
          </p>
        </div>;
    }
    if (isHormonalProfileTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-5361">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5362">
            Hormonal Profile Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-5363">
            <thead className="bg-gray-100" data-id="element-5364">
              <tr data-id="element-5365">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5366">Test</th>
                <th className="border border-gray-300 p-1.5 text-left w-28" data-id="element-5367">
                  Value
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-5368">
                  Reference Range
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-16" data-id="element-5369">
                  Unit
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5370">
              {hormonalProfileTests.map(h => <tr key={h.key} data-id="element-5371">
                  <td className="border border-gray-300 p-1.5 font-medium" data-id="element-5372">
                    {h.name}
                  </td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5373">
                    {lab.results![`HP_${h.key}`] || '—'}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5374">
                    {h.ranges.map(r => <div key={r.label} data-id="element-5375">
                        <span className="font-medium" data-id="element-5376">{r.label}:</span>{' '}
                        {r.value}
                      </div>)}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-5377">
                    {h.unit}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>;
    }
    // Fallback: generic key/value table
    return <div className="text-sm" data-id="element-5378">
        <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5379">
          {activeTests.join(', ')} Report
        </h4>
        <table className="w-full text-xs border border-gray-300" data-id="element-5380">
          <thead className="bg-gray-100" data-id="element-5381">
            <tr data-id="element-5382">
              <th className="border border-gray-300 p-1.5 text-left" data-id="element-5383">
                Parameter
              </th>
              <th className="border border-gray-300 p-1.5 text-left" data-id="element-5384">Result</th>
            </tr>
          </thead>
          <tbody data-id="element-5385">
            {Object.entries(lab.results).map(([k, v]) => v ? <tr key={k} data-id="element-5386">
                  <td className="border border-gray-300 p-1.5" data-id="element-5387">{k}</td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-5388">
                    {v}
                  </td>
                </tr> : null)}
          </tbody>
        </table>
      </div>;
  };
  const renderRadReport = (r: RadiologyRequest) => <div className="text-sm" data-id="element-5389">
      <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-5390">
        Radiology Report — {r.scanType}
      </h4>
      {r.completedAt && <p className="text-xs text-gray-500 mb-2" data-id="element-5391">
          Completed: {new Date(r.completedAt).toLocaleString()}
        </p>}
      <div className="bg-gray-50 border border-gray-200 rounded p-3 whitespace-pre-wrap text-sm" data-id="element-5392">
        {r.report || 'No report available.'}
      </div>
      {r.images && r.images.length > 0 && <div className="mt-3 grid grid-cols-2 gap-2" data-id="element-5393">
          {r.images.map((img, idx) => <img key={idx} src={img} alt={`${r.scanType} ${idx + 1}`} className="rounded border border-gray-200 object-cover w-full max-h-64" data-id="element-5394" />)}
        </div>}
    </div>;
  return <>
      <Card data-id="element-5395">
        <div className="flex justify-between items-center mb-4 print:hidden" data-id="element-5396">
          <div className="flex items-center gap-2 flex-1 max-w-md" data-id="element-5397">
            <div className="relative w-full" data-id="element-5398">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5399">
                <Search className="h-4 w-4 text-gray-400" data-id="element-5400" />
              </div>
              <input type="text" placeholder="Search by patient name or BMC..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" data-id="element-5401" />
            </div>
          </div>
          <Badge variant="info" className="text-sm" data-id="element-5402">
            {filtered.length} report{filtered.length === 1 ? '' : 's'}
          </Badge>
        </div>

        {filtered.length === 0 ? <div className="text-center py-12 text-gray-500" data-id="element-5403">
            <FileText className="mx-auto h-12 w-12 text-gray-300 mb-3" data-id="element-5404" />
            <p className="text-lg font-medium text-gray-900" data-id="element-5405">No reports yet</p>
            <p className="text-sm" data-id="element-5406">
              Completed lab and radiology reports will appear here.
            </p>
          </div> : <ul className="divide-y divide-gray-200" data-id="element-5407">
            {filtered.map(enc => {
          const patient = getPatient(enc.patientId);
          if (!patient) return null;
          const completedLabs = enc.labRequests.filter(l => l.status === 'Completed');
          const completedRads = enc.radiologyRequests.filter(r => r.report || r.images && r.images.length > 0);
          return <li key={enc.id} className="py-4 flex justify-between items-start gap-4" data-id="element-5408">
                  <div className="flex-1" data-id="element-5409">
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-5410">
                      <p className="text-base font-bold text-gray-900" data-id="element-5411">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <Badge variant="info" data-id="element-5412">BMC: {patient.bmcFileNumber}</Badge>
                      <span className="text-xs text-gray-500" data-id="element-5413">
                        {patient.age} yrs · {patient.sex}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-5414">
                      Visit: {new Date(enc.startedAt).toLocaleString()}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2" data-id="element-5415">
                      {completedLabs.length > 0 && <span className="inline-flex items-center text-xs bg-yellow-50 text-yellow-800 px-2 py-1 rounded border border-yellow-200" data-id="element-5416">
                          <FlaskConical className="w-3 h-3 mr-1" data-id="element-5417" />
                          {completedLabs.length} Lab Report
                          {completedLabs.length > 1 ? 's' : ''}
                        </span>}
                      {completedRads.length > 0 && <span className="inline-flex items-center text-xs bg-purple-50 text-purple-800 px-2 py-1 rounded border border-purple-200" data-id="element-5418">
                          <Scan className="w-3 h-3 mr-1" data-id="element-5419" />
                          {completedRads.length} Radiology Report
                          {completedRads.length > 1 ? 's' : ''}
                        </span>}
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => setViewing({
              encounter: enc
            })} data-id="element-5420">
                    <FileText className="w-4 h-4 mr-2" data-id="element-5421" /> View Report
                  </Button>
                </li>;
        })}
          </ul>}
      </Card>

      {viewing && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 z-50 overflow-y-auto" data-id="element-5422">
          <div className="min-h-full flex items-start justify-center p-4" data-id="element-5423">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[calc(100vh-2rem)] flex flex-col my-4" data-id="element-5424">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0 sticky top-0 bg-white z-10 print:hidden" data-id="element-5425">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-5426">
                  Patient Report
                </h3>
                <div className="flex items-center gap-2" data-id="element-5427">
                  <Button variant="outline" onClick={handlePrint} data-id="element-5428">
                    <Printer className="w-4 h-4 mr-2" data-id="element-5429" /> Print
                  </Button>
                  <Button variant="outline" onClick={handleDownloadPDF} data-id="element-5430">
                    <Download className="w-4 h-4 mr-2" data-id="element-5431" /> Download PDF
                  </Button>
                  <button onClick={() => setViewing(null)} data-id="element-5432">
                    <X className="w-5 h-5 text-gray-400" data-id="element-5433" />
                  </button>
                </div>
              </div>
              <div id="report-printable" className="p-6 overflow-y-auto flex-1" data-id="element-5434">
                {(() => {
              const patient = getPatient(viewing.encounter.patientId);
              if (!patient) return null;
              return <>
                      <div className="border-b-2 border-black pb-3 mb-4" data-id="element-5435">
                        <h2 className="text-xl font-bold uppercase" data-id="element-5436">
                          Patient Report
                        </h2>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-sm" data-id="element-5437">
                          <p data-id="element-5438">
                            <span className="font-semibold" data-id="element-5439">Patient:</span>{' '}
                            {patient.firstName} {patient.lastName}
                          </p>
                          <p data-id="element-5440">
                            <span className="font-semibold" data-id="element-5441">BMC #:</span>{' '}
                            {patient.bmcFileNumber}
                          </p>
                          <p data-id="element-5442">
                            <span className="font-semibold" data-id="element-5443">Age:</span>{' '}
                            {patient.age} yrs
                          </p>
                          <p data-id="element-5444">
                            <span className="font-semibold" data-id="element-5445">Sex:</span>{' '}
                            {patient.sex}
                          </p>
                          <p className="col-span-2" data-id="element-5446">
                            <span className="font-semibold" data-id="element-5447">Visit date:</span>{' '}
                            {new Date(viewing.encounter.startedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {viewing.encounter.labRequests.filter(l => l.status === 'Completed').map(lab => <div key={lab.id} className="mb-6" data-id="element-5448">
                            {renderLabResults(lab)}
                            {lab.labComments && <div className="mt-4 border border-gray-300 bg-gray-50 rounded p-3" data-id="element-5449">
                                <h5 className="font-bold uppercase mb-1 text-xs" data-id="element-5450">
                                  Comments / Observations
                                </h5>
                                <pre className="whitespace-pre-wrap font-sans text-xs text-gray-800" data-id="element-5451">
                                  {lab.labComments}
                                </pre>
                              </div>}
                          </div>)}

                      {viewing.encounter.radiologyRequests.filter(r => r.report || r.images && r.images.length > 0).map(r => <div key={r.id} className="mb-6" data-id="element-5452">
                            {renderRadReport(r)}
                          </div>)}

                      {viewing.encounter.labRequests.filter(l => l.status === 'Completed').length === 0 && viewing.encounter.radiologyRequests.filter(r => r.report).length === 0 && <p className="text-gray-500 text-center py-8" data-id="element-5453">
                            No reports available for this encounter.
                          </p>}
                    </>;
            })()}
              </div>
            </div>
          </div>
        </div>}

      {/* Print-specific styling */}
      <style data-id="element-5454">{`
        @media print {
          body * { visibility: hidden; }
          #report-printable, #report-printable * { visibility: visible; }
          #report-printable { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
        }
      `}</style>
    </>;
};